import { MatterExpertStatus } from '~/gql/graphql';
import type { MatterExpertRow } from '~/modules/matters/matters.types';

function getMatterExpertRows(
  matterExperts: readonly MatterExpertRow[] | null | undefined,
): readonly MatterExpertRow[] {
  return matterExperts ?? [];
}

/** Invited or hired: expert is actively associated with the matter. */
export const MATTER_EXPERT_ACTIVE_PARTICIPATION_STATUSES: ReadonlySet<MatterExpertStatus> =
  new Set([MatterExpertStatus.Invited, MatterExpertStatus.Hired]);

export function getHiredExpertIds(
  matterExperts: readonly MatterExpertRow[] | null | undefined,
): string[] {
  return getMatterExpertRows(matterExperts)
    .filter((row) => row.status === MatterExpertStatus.Hired)
    .map((row) => row.expertId);
}

export function getInvitedExpertIds(
  matterExperts: readonly MatterExpertRow[] | null | undefined,
): string[] {
  return getMatterExpertRows(matterExperts)
    .filter((row) => row.status === MatterExpertStatus.Invited)
    .map((row) => row.expertId);
}

/** Expert ids already hired or invited on the matter (deduped). */
export function getHiredAndInvitedExpertIds(
  matterExperts: readonly MatterExpertRow[] | null | undefined,
): string[] {
  return [
    ...new Set([
      ...getHiredExpertIds(matterExperts),
      ...getInvitedExpertIds(matterExperts),
    ]),
  ];
}

/** First matter-expert row with hired status (array order). */
export function getFirstHiredMatterExpertRow(
  matterExperts: readonly MatterExpertRow[] | null | undefined,
): MatterExpertRow | undefined {
  return getMatterExpertRows(matterExperts).find(
    (row) => row.status === MatterExpertStatus.Hired,
  );
}

export function getFirstHiredExpertId(
  matterExperts: readonly MatterExpertRow[] | null | undefined,
): string | undefined {
  return getFirstHiredMatterExpertRow(matterExperts)?.expertId;
}

export function getFirstInvitedExpertId(
  matterExperts: readonly MatterExpertRow[] | null | undefined,
): string | undefined {
  return getMatterExpertRows(matterExperts).find(
    (row) => row.status === MatterExpertStatus.Invited,
  )?.expertId;
}

export function isHiredExpertOnMatter(
  matterExperts: readonly MatterExpertRow[] | null | undefined,
  expertId: string,
): boolean {
  return getMatterExpertRows(matterExperts).some(
    (row) =>
      row.status === MatterExpertStatus.Hired && row.expertId === expertId,
  );
}

export function isInvitedExpertOnMatter(
  matterExperts: readonly MatterExpertRow[] | null | undefined,
  expertId: string,
): boolean {
  return getMatterExpertRows(matterExperts).some(
    (row) =>
      row.status === MatterExpertStatus.Invited && row.expertId === expertId,
  );
}

/**
 * Row used for `createRetainerRequest`: expert must be invited or hired.
 * If both exist (unexpected), prefers the hired row.
 */
export function getMatterExpertRowForRetainerRequest(
  matterExperts: readonly MatterExpertRow[] | null | undefined,
  expertId: string,
): MatterExpertRow | undefined {
  const rows = getMatterExpertRows(matterExperts).filter(
    (row) =>
      row.expertId === expertId &&
      MATTER_EXPERT_ACTIVE_PARTICIPATION_STATUSES.has(row.status),
  );
  const hiredRow = rows.find((row) => row.status === MatterExpertStatus.Hired);
  return hiredRow ?? rows[0];
}

export function hasAnyHiredExpert(
  matterExperts: readonly MatterExpertRow[] | null | undefined,
): boolean {
  return getMatterExpertRows(matterExperts).some(
    (row) => row.status === MatterExpertStatus.Hired,
  );
}

/** True when the expert has an invited or hired row (not declined/removed only). */
export function expertHasActiveParticipationInMatterExperts(
  matterExperts: readonly MatterExpertRow[] | null | undefined,
  expertId: string,
): boolean {
  return getMatterExpertRows(matterExperts).some(
    (row) =>
      row.expertId === expertId &&
      MATTER_EXPERT_ACTIVE_PARTICIPATION_STATUSES.has(row.status),
  );
}
